import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormat } from '@/i18n/useFormat';
import { useLocale } from '@/i18n/useLocale';
import { useSession } from '@/stores/session';
import { paths } from '@/app/paths';
import { Dialog, ErrorState } from '@/ui';
import { getSetBetHandler, installGameErrorInterceptor, useGameError, type GameErrorView } from './gameErrors';
import s from './errors.module.css';

/** Renders the designed error of `useGameError` (modal on desktop, sheet on phones). Mounted once in the app chrome. */
export function GameErrorHost() {
  const current = useGameError((st) => st.current);
  const clear = useGameError((st) => st.clear);
  const intl = useIntl();
  useEffect(() => installGameErrorInterceptor(), []);
  return (
    <Dialog open={current !== null} onClose={clear} width={440} label={intl.formatMessage({ id: 'common.errorState.label' })}>
      {current && <GameErrorBody view={current} onDone={clear} />}
    </Dialog>
  );
}

export function GameErrorBody({ view, onDone }: { view: GameErrorView; onDone(): void }) {
  const f = useFormat();
  const l = useLocale();
  const navigate = useNavigate();
  const openDaily = useSession((st) => st.openDaily);
  const close = { label: <FormattedMessage id="common.close" />, onClick: onDone };
  const toLimits = {
    label: <FormattedMessage id="common.errorState.limits.open" />,
    onClick: () => {
      onDone();
      navigate(paths.responsible(l));
    },
  };

  if (view.kind === 'balance') {
    const setBet = getSetBetHandler();
    const need = view.need ?? view.have;
    return (
      <ErrorState
        className={s.inDialog}
        glyph="0"
        tone="orange"
        title={<FormattedMessage id="errors.INSUFFICIENT_BALANCE" />}
        text={<FormattedMessage id="common.errorState.noBalance.text" values={{ need: f.int(need), num: need, have: f.int(view.have) }} />}
        primary={{
          label: <FormattedMessage id="common.errorState.noBalance.claim" />,
          onClick: () => {
            onDone();
            openDaily(true);
          },
        }}
        secondary={
          setBet && view.have > 0
            ? {
                label: <FormattedMessage id="common.errorState.noBalance.setBet" values={{ have: f.int(view.have) }} />,
                onClick: () => {
                  setBet(view.have);
                  onDone();
                },
              }
            : close
        }
      />
    );
  }
  if (view.kind === 'limitTokens') {
    return (
      <ErrorState
        className={s.inDialog}
        glyph="||"
        tone="orange"
        title={<FormattedMessage id="errors.LIMIT_TOKENS_REACHED" />}
        text={<FormattedMessage id="common.errorState.limits.tokens" values={{ used: f.int(view.used), limit: f.int(view.limit), num: view.limit }} />}
        primary={toLimits}
        secondary={close}
      />
    );
  }
  if (view.kind === 'limitTime') {
    return (
      <ErrorState
        className={s.inDialog}
        glyph="||"
        tone="orange"
        title={<FormattedMessage id="errors.LIMIT_TIME_REACHED" />}
        text={<FormattedMessage id="common.errorState.limits.time" values={{ minutes: view.limit }} />}
        primary={toLimits}
        secondary={close}
      />
    );
  }
  return (
    <ErrorState
      className={s.inDialog}
      glyph="||"
      tone="orange"
      title={<FormattedMessage id="errors.ON_BREAK" />}
      text={
        view.until ? (
          <FormattedMessage id="common.errorState.limits.breakUntil" values={{ until: f.date(view.until, { dateStyle: 'medium', timeStyle: 'short' }) }} />
        ) : (
          <FormattedMessage id="common.errorState.limits.break" />
        )
      }
      primary={toLimits}
      secondary={close}
    />
  );
}
